/**
 * Lane 2 — repeat buyer ("another card, same person").
 *
 * Journey-map contract:
 *  - Style gallery: ALWAYS shown, already-owned styles marked.
 *  - Selfie: OPTIONAL. The stored avatar still fits any style; a retake is
 *    offered, never forced. A retake is a fresh capture and needs FRESH
 *    biometric consent.
 *  - Email + OTP: SKIPPED. The session is already authenticated.
 *  - Biometric consent: SKIPPED only while reusing the stored template.
 *  - KYC details: CONFIRMED from what's on file, not re-typed.
 *  - Authorisation: ALWAYS. Every card is its own hold and its own bank
 *    decision.
 *
 * Repeat mode is a client-side routing mode only — it never relaxes a
 * server-side gate. `attach_selfie_to_user` still refuses without a granted
 * user-scope biometric consent, and submit-card still validates everything.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { startNewOrderEpoch } from "@/lib/generation-session";
import { clearCardCustomization, clearDetails } from "@/lib/consumer-session";
import { clearDesignedCard } from "@/lib/designed-card";

const REPEAT_KEY = (slug: string) => `cmoji.consumer.repeat.${slug}`;

export function setRepeatMode(slug: string, on: boolean) {
  try {
    if (on) localStorage.setItem(REPEAT_KEY(slug), "1");
    else localStorage.removeItem(REPEAT_KEY(slug));
  } catch {
    /* storage unavailable */
  }
}

export function isRepeatMode(slug: string): boolean {
  try {
    return localStorage.getItem(REPEAT_KEY(slug)) === "1";
  } catch {
    return false;
  }
}

/** Entry points A/B/C from the journey map all funnel through here. */
export function repeatEntryPath(slug: string): string {
  setRepeatMode(slug, true);
  // A repeat order is a NEW order, not a continuation. Without this, the tab's
  // generation ledger, the staged card customisation (colour/zoom/position) and
  // the canonical DesignedCard from the PREVIOUS card all survive into the new
  // order — which is what produced the accumulated avatar set (4 shown, some of
  // them last order's raw rows) and a card that opened on the old colour and
  // framing instead of the new pick.
  startNewOrderEpoch(slug);
  clearCardCustomization(slug);
  clearDesignedCard(slug);
  // The staged details draft holds intended_load_amount_cents — a value handed
  // to the ISSUING BANK. Carrying the previous order's load into a new order is
  // a fabricated assertion of intent, so the draft is dropped and the reorder
  // must capture the load amount explicitly (see S6Details).
  clearDetails(slug);
  return "/create/style";
}

export interface RepeatKyc {
  first_name: string;
  middle_name: string | null;
  last_name: string;
  phone: string | null;
  /**
   * `YYYY-MM-DD`. Carried so a returning customer does not retype identity data
   * we already hold. KYC/OFAC verification only — never age-derived.
   */
  date_of_birth: string | null;
  delivery_type: string;
  postal_address: Record<string, string> | null;
  card_color: string | null;
  orientation: string | null;
}


export interface RepeatProfile {
  /** Repeat mode flag AND a signed-in user — both are required. Drives COPY. */
  isRepeat: boolean;
  /**
   * ROUTING truth: is this person already identified (a live auth session)?
   *
   * Distinct from `isRepeat` on purpose. `isRepeat` is "arrived from the account
   * surface to buy another card" and controls copy/step counts. `isIdentified`
   * is simply "has a session", and it is what the funnel must consult before
   * sending anyone to S3 (email + OTP).
   *
   * Why: a FIRST-order buyer verifies at S3 mid-funnel. If they then walk BACK
   * to S1 to change the style, the repeat FLAG is not set, so the old
   * flag-gated routing treated them as anonymous and marched them through
   * selfie + email + OTP again — re-identifying an already-verified session and
   * invalidating the OTP they were holding. Identity is session state, never a
   * localStorage mode flag.
   */
  isIdentified: boolean;
  loading: boolean;
  userId: string | null;
  /** A stored avatar template exists and consent for it was granted. */
  storedSelfiePath: string | null;
  biometricConsentAt: string | null;
  /** style ids the person already holds a card for. */
  ownedStyleIds: string[];
  /** Most recent details on file — the "confirm, don't re-type" source. */
  kyc: RepeatKyc | null;
}

const EMPTY: RepeatProfile = {
  isRepeat: false,
  isIdentified: false,
  loading: false,
  userId: null,
  storedSelfiePath: null,
  biometricConsentAt: null,
  ownedStyleIds: [],
  kyc: null,
};

export function useRepeatProfile(slug: string): RepeatProfile {
  const repeatFlag = isRepeatMode(slug);
  // Loading starts true for EVERYONE now: the answer to "is this person already
  // identified?" needs the session, and routing must not be decided on a
  // premature "no".
  const [state, setState] = useState<RepeatProfile>({ ...EMPTY, isRepeat: repeatFlag, loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        // No session: anonymous Lane 1, whatever the flag says.
        if (!cancelled) setState({ ...EMPTY, isRepeat: false, isIdentified: false, loading: false });
        return;
      }

      const [{ data: prof }, { data: rows }, { data: acct }] = await Promise.all([
        supabase
          .from("consumer_profiles")
          .select("selfie_storage_path, biometric_consent_at, selfie_purged_at")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("card_requests")
          .select("style, first_name, middle_name, last_name, phone, date_of_birth, delivery_type, postal_address, card_color, orientation, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false }),
        // The PROFILE is the authoritative default for name + address. It is
        // what My Account edits, and an edit there must show up on the next
        // order — otherwise the customer "changes their address" and the
        // wizard silently keeps prefilling the old one from the last order.
        supabase
          .from("profiles")
          .select("first_name, last_name, phone, dob, postal_address, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const list = (rows as any[]) || [];
      const latest = list[0];
      const purged = Boolean((prof as any)?.selfie_purged_at);
      const p = acct as any;

      setState({
        // Copy/step-count mode still requires the explicit reorder entry.
        isRepeat: repeatFlag,
        // Routing truth: a session exists.
        isIdentified: true,
        loading: false,
        userId: user.id,
        storedSelfiePath: purged ? null : ((prof as any)?.selfie_storage_path ?? null),
        biometricConsentAt: (prof as any)?.biometric_consent_at ?? null,
        ownedStyleIds: Array.from(new Set(list.map((r) => r.style).filter(Boolean))),
        /**
         * PRECEDENCE: profile default first, latest order as FALLBACK.
         *
         * Only the fields the profile actually owns are overridden. Everything
         * else stays per-order:
         *  - delivery_type / branch_id: per-order only, never a profile default.
         *    A previous branch collection says nothing about this order, and
         *    S6 re-asks. (postal_address is still carried, so a mail order
         *    prefills even if the last one was a branch pickup.)
         *  - pan_last5 and intended_load_amount_cents: never inherited at all —
         *    they are not in RepeatKyc and S6 keeps them required. That
         *    structural fix is upstream of this precedence change.
         */
        kyc: latest || p
          ? {
              first_name: p?.first_name || latest?.first_name || "",
              // The middle name is per-order truth: it is printed on the card
              // face, and the profile does not own it.
              middle_name: latest?.middle_name ?? null,
              last_name: p?.last_name || latest?.last_name || "",
              // Profile-owned contact/identity first, last order as fallback —
              // same precedence rule as name and address.
              phone: p?.phone || latest?.phone || null,
              date_of_birth: p?.dob || latest?.date_of_birth || null,
              delivery_type: latest?.delivery_type ?? "",
              postal_address: (p?.postal_address as any) ?? latest?.postal_address ?? null,
              card_color: latest?.card_color ?? null,
              orientation: latest?.orientation ?? null,
            }
          : null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, repeatFlag]);

  return state;
}

/**
 * THE one place the postal address is turned into human-readable text.
 *
 * FIELD-NAME CONTRACT: `region` is the key S6 WRITES (see commit() in
 * S6Details); `state` is the legacy key from the older wizard, and `postcode`
 * vs `zip` is the same split. Reading only `state` made every recent record's
 * State silently vanish from the summary even when it was on file, which is why
 * this resolution must not be duplicated at each call site.
 */
export function postalAddressSummary(
  address: Record<string, string> | null | undefined,
  deliveryType?: string | null,
): string | null {
  if (deliveryType === "branch" || deliveryType === "in_branch") return "Collect at your branch";
  if (!address) return null;
  const region = address.region || address.state;
  const postcode = address.postcode || address.zip;
  return (
    [address.line1, address.line2, address.city, region, postcode].filter(Boolean).join(", ") || null
  );
}

/** One-line address summary for the "details on file" confirm card. */
export function addressSummary(kyc: RepeatKyc | null): string | null {
  if (!kyc) return null;
  return postalAddressSummary(
    kyc.postal_address as Record<string, string> | null,
    kyc.delivery_type,
  );
}

